'use client';

import React from 'react';
import { Button } from '../../ui/button';
import Dropdown from '../../common/dropdown';
import Image from 'next/image';
import type { CountryLoginItem } from '@/hooks/auth/sso/useLoginData';

interface SSOSectionProps {
  countryLoginItems: CountryLoginItem[];
  uniqueRegions: string[];
  selectedRegion: string;
  onRegionChange: (region: string) => void;
  onSSOClick: (regionCode: string) => void;
  isLoading?: boolean;
  /** Bottom divider caption. Omit for no bottom divider. */
  dividerText?: string;
}

/**
 * The horizontal rule used four times in this file (plain after the dropdown, captioned at
 * the bottom, once each in the real layout and the loading skeleton). One component so a
 * style change is one edit and the skeleton cannot drift from what it stands in for.
 */
function Divider({ text }: { text?: string }) {
  return (
    <div className="flex items-center gap-2 sm:gap-4 my-8">
      <div className="flex-1 h-px bg-border-medium"></div>
      {text && <span className="cs-h6">{text}</span>}
      {text && <div className="flex-1 h-px bg-border-medium"></div>}
    </div>
  );
}

export function SSOSection({
  countryLoginItems,
  uniqueRegions,
  selectedRegion,
  onRegionChange,
  onSSOClick,
  isLoading = false,
  dividerText,
}: SSOSectionProps) {
  // Filter services by selected title (region) and group by heading to get unique services
  const uniqueServices = React.useMemo(() => {
    if (!selectedRegion) return [];

    // First, filter by selected title (region)
    const filteredByRegion = countryLoginItems.filter(
      (item) => item.title === selectedRegion
    );

    // Then, group by heading to get unique services
    const servicesMap = new Map<string, CountryLoginItem>();
    filteredByRegion.forEach((item) => {
      if (item.heading && !servicesMap.has(item.heading)) {
        servicesMap.set(item.heading, item);
      }
    });

    return Array.from(servicesMap.values());
  }, [countryLoginItems, selectedRegion]);

  const regionOptions = React.useMemo(
    () => uniqueRegions.map((region) => ({ value: region, label: region })),
    [uniqueRegions]
  );

  // Loading skeleton
  if (isLoading) {
    return (
      <>
        {/* Loading skeleton for region dropdown */}
        <div className="flex flex-col gap-2 mb-6 sm:mb-[31px]">
          <label htmlFor="region" className="cs-input-field">
            Choose your region
          </label>
          <div className="min-h-[40px] bg-surface-3 animate-pulse rounded"></div>
        </div>

        <Divider />

        {/* Loading skeleton for buttons */}
        <div className="flex flex-col gap-6 sm:gap-[31px] pb-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="min-h-[40px] bg-surface-3 animate-pulse rounded"></div>
          ))}
        </div>

        {dividerText && <Divider text={dividerText} />}
      </>
    );
  }

  if (uniqueRegions.length === 0) return null;

  return (
    <>
      {/* Region Selection */}
      <div className="flex flex-col gap-2">
        <label htmlFor="region" className="cs-input-field">
          Choose your region
        </label>
        <Dropdown
          options={regionOptions}
          defaultValue={selectedRegion}
          onChange={(value) => onRegionChange(value as string)}
          className="bg-surface-5 cs-input-field hover:bg-surface-4 touch-manipulation cursor-pointer"
        />
      </div>

      <Divider />

      {/* SSO Buttons - Unique services grouped by heading */}
      {uniqueServices.length > 0 && (
        <div className="flex flex-col gap-8">
          {uniqueServices.map((item) => (
            <div key={`${item.heading}-${item.region_code}`}>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  if (item.region_code) {
                    onSSOClick(item.region_code);
                  }
                }}
                variant="SecondaryFillButton"
                className="px-3 py-4 !h-auto w-full bg-surface-5 flex items-center font-normal justify-center gap-3 touch-manipulation btn-no-hover"
              >
                {item.icon && (
                  <Image
                    width={22}
                    height={30}
                    src={item.icon.url}
                    alt={item.icon.title || item.heading || item.title || ''}
                    className="flex-shrink-0"
                  />
                )}
                <span className="cs-input-field">{item.heading || item.link?.title || item.title || ''}</span>
              </Button>
            </div>
          ))}
        </div>
      )}

      {dividerText && <Divider text={dividerText} />}
    </>
  );
}
